import type { FormEvent } from "react";

import {
    Button,
    LanguageSelect,
    SenioritySelect,
    Tooltip,
} from "@/atoms";
import { strings } from "@/constants";
import {
    BenchmarkPageStatusEnum,
    ButtonTypeEnum,
    ButtonVariantEnum,
    InputTypeEnum,
} from "@/enums";
import type { BenchmarkPageStatusType, LanguageType } from "@/types";

const buttonTextHandler = (status: BenchmarkPageStatusType): string => {
    if (status === BenchmarkPageStatusEnum.LOADING) return strings.benchmark.buttonLoading;

    if (status === BenchmarkPageStatusEnum.ERROR) return strings.benchmark.buttonReenter;

    return strings.benchmark.buttonGenerate;
};

export const BenchmarkForm = ({
    industry,
    isFormValid,
    jobDescription,
    jobTitle,
    lang,
    onChangeIndustry,
    onChangeJobDescription,
    onChangeJobTitle,
    onChangeLang,
    onChangeSeniority,
    onReset,
    onSubmit,
    seniority,
    status,
}: {
    industry: string,
    isFormValid: boolean,
    jobDescription: string,
    jobTitle: string,
    lang: LanguageType,
    onChangeIndustry: (value: string) => void,
    onChangeJobDescription: (value: string) => void,
    onChangeJobTitle: (value: string) => void,
    onChangeLang: (value: LanguageType) => void,
    onChangeSeniority: (value: string) => void,
    onReset: () => void,
    onSubmit: (event: FormEvent<HTMLFormElement>) => void,
    seniority: string,
    status: BenchmarkPageStatusType,
}) => {
    const isError = status === BenchmarkPageStatusEnum.ERROR;

    const isLoading = status === BenchmarkPageStatusEnum.LOADING;

    const fieldBoxClass = `
        px-5
        py-3.5
        w-full
        text-base
        text-text-primary
        bg-white
        border-2
        outline-none
        rounded-2xl
        duration-200
        transition-all
        placeholder:text-text-muted/60
        disabled:cursor-not-allowed
        disabled:opacity-60
        focus:border-primary
        focus:shadow-[0_0_0_4px_rgba(19,86,188,0.10)]
        ${isError ? "border-error/40" : "border-primary/15"}
    `;

    const labelClass = `
        flex
        items-center
        gap-2
        mb-2
        font-semibold
        text-[11px]
        text-text-primary/70
        tracking-[0.22em]
        uppercase
    `;

    const fieldNumberClass = "font-mono text-primary/45 tabular-nums";

    return (
        <form
            className="
                form-rise
                mx-auto
                w-full
                max-w-2xl
                text-left
            "
            onSubmit={onSubmit}
        >
            <div
                className={`
                    relative
                    rounded-3xl
                    border
                    bg-surface
                    px-6
                    pt-7
                    pb-5
                    shadow-[0_30px_70px_-45px_rgba(0,37,186,0.45)]
                    sm:px-9
                    sm:pt-9
                    sm:pb-6
                    ${isError ? "border-error/30" : "border-primary/10"}
                `}
            >
                <div
                    className="
                        absolute
                        inset-x-0
                        top-0
                        h-px
                        bg-gradient-to-r
                        from-transparent
                        via-primary/40
                        to-transparent
                    "
                />
                <div
                    className="form-rise mb-5"
                    style={{ animationDelay: "80ms" }}
                >
                    <label
                        className={labelClass}
                        htmlFor="jobTitle"
                    >
                        <span className={fieldNumberClass}>01</span>
                        <span>{strings.benchmark.jobTitleLabel}</span>
                        <span
                            className="text-primary"
                            aria-hidden
                        >
                            {strings.benchmark.requiredAsterisk}
                        </span>
                    </label>
                    <input
                        className={fieldBoxClass}
                        disabled={isLoading}
                        id="jobTitle"
                        placeholder={strings.benchmark.jobTitlePlaceholder}
                        type={InputTypeEnum.TEXT}
                        value={jobTitle}
                        onChange={({ target }) => onChangeJobTitle(target.value)}
                    />
                </div>
                <div
                    className="form-rise mb-5"
                    style={{ animationDelay: "150ms" }}
                >
                    <label
                        className={labelClass}
                        htmlFor="industry"
                    >
                        <span className={fieldNumberClass}>02</span>
                        <span>{strings.benchmark.industryLabel}</span>
                        <span
                            className="text-primary"
                            aria-hidden
                        >
                            {strings.benchmark.requiredAsterisk}
                        </span>
                    </label>
                    <input
                        className={fieldBoxClass}
                        disabled={isLoading}
                        id="industry"
                        placeholder={strings.benchmark.industryPlaceholder}
                        type={InputTypeEnum.TEXT}
                        value={industry}
                        onChange={({ target }) => onChangeIndustry(target.value)}
                    />
                </div>
                <div
                    className="form-rise mb-5"
                    style={{ animationDelay: "220ms" }}
                >
                    <label
                        className={labelClass}
                        htmlFor="seniority"
                    >
                        <span className={fieldNumberClass}>03</span>
                        <span>{strings.benchmark.seniorityLabel}</span>
                        <span
                            className="text-primary"
                            aria-hidden
                        >
                            {strings.benchmark.requiredAsterisk}
                        </span>
                    </label>
                    <SenioritySelect
                        fieldClassName={fieldBoxClass}
                        id="seniority"
                        isDisabled={isLoading}
                        isError={isError}
                        selectedValue={seniority}
                        onChange={onChangeSeniority}
                    />
                </div>
                <div
                    className="form-rise"
                    style={{ animationDelay: "290ms" }}
                >
                    <label
                        className={labelClass}
                        htmlFor="jobDescription"
                    >
                        <span className={fieldNumberClass}>04</span>
                        <span>{strings.benchmark.jobDescriptionLabel}</span>
                        <span
                            className="text-primary"
                            aria-hidden
                        >
                            {strings.benchmark.requiredAsterisk}
                        </span>
                    </label>
                    <textarea
                        className={`resize-none leading-relaxed ${fieldBoxClass}`}
                        disabled={isLoading}
                        id="jobDescription"
                        placeholder={strings.benchmark.jobDescriptionPlaceholder}
                        rows={3}
                        value={jobDescription}
                        onChange={({ target }) => onChangeJobDescription(target.value)}
                    />
                </div>
                <div
                    className="
                        mt-5
                        flex
                        flex-col
                        items-stretch
                        gap-4
                        border-t
                        border-primary/10
                        pt-4
                        sm:flex-row
                        sm:items-center
                        sm:justify-between
                    "
                >
                    <LanguageSelect
                        isDisabled={isLoading}
                        value={lang}
                        onChange={onChangeLang}
                    />
                    <Tooltip
                        content={strings.benchmark.formValidationHint}
                        isDisabled={isFormValid || isError || isLoading}
                    >
                        <Button
                            disabled={isLoading || (status === BenchmarkPageStatusEnum.IDLE && !isFormValid)}
                            isLoading={isLoading}
                            type={isError ? ButtonTypeEnum.BUTTON : ButtonTypeEnum.SUBMIT}
                            variant={isError || isFormValid ? ButtonVariantEnum.PRIMARY : ButtonVariantEnum.MUTED}
                            className="
                                tracking-[0.16em]
                                uppercase
                                hover:-translate-y-0.5
                                hover:shadow-[0_18px_40px_-15px_rgba(0,37,186,0.55)]
                            "
                            onClick={isError ? onReset : undefined}
                        >
                            {buttonTextHandler(status)}
                        </Button>
                    </Tooltip>
                </div>
            </div>
        </form>
    );
};
